import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { SpecialtyMetrics, SpecialtyRow } from '../../api/types.js';
import { color, font, fontSize, radius, seriesColor, space } from '../../theme/tokens.js';
import { formatNumber } from '../MetricCell/format.js';
import { DataTable, type Column } from '../DataTable/DataTable.js';
import { MetricCell } from '../MetricCell/MetricCell.js';

/**
 * The eleven-column discipline table (FR-S10, blueprint § Specialties).
 *
 * The column set is the blueprint's, exactly. Six families a reader might expect are absent and
 * each is a recorded decision, not an oversight:
 *
 * - **Subs / Ofrs / Strt** — period-scoped, so they would force a Timeline control onto a page
 *   whose every other figure is an as-of snapshot, putting an all-time and a last-quarter figure
 *   in one row (FR-S17).
 * - **Jobs** — market demand rather than the book; reported by region instead (FR-S17).
 * - **GM** — matched to only ~77% of active placements, so it would dash two-thirds of rows.
 * - **Cov % / Conv %** — a rate on a denominator of three open positions is noise.
 * - **Recruiters** — not merely omitted but *impossible*: a recruiter is typed Nursing or Allied
 *   and never by specialty, so "the recruiter count for a discipline" does not exist (FR-S18).
 * - **Trend arrows** — the blueprint's Specialties table renders plain figures, and this page
 *   holds no prior window to compare against (FR-S16).
 */

export interface SpecialtyTableProps {
  rows: SpecialtyRow[];
  /** Total rows that exist, from the server — backs the off-page counts (FR-S12). */
  rowCount: number;
  selectedKey: string | null;
  onSelect: (row: SpecialtyRow) => void;
  /** Rows per page. */
  pageSize?: number;
  emptyMessage?: string;
}

export const specialtyRowKey = (row: SpecialtyRow): string =>
  `${row.profession}|${row.specialty}`;

/**
 * Rows per page (owner decision, 2026-08-26).
 *
 * ⚠ This **replaces** FR-S11's "top forty, expandable to all" model. Pagination reaches every
 * row in a bounded number of clicks, which is what the reveal control existed to provide, so the
 * two would only compete.
 *
 * One consequence worth naming: the unrecorded-specialty rows are **no longer pinned**. Pinning
 * existed to stop a hard forty-row cut from burying the page's largest data-quality signal behind
 * a *Show all* click. Nothing is buried now — the biggest such row ranks third by book size and
 * lands on page one on its own merits, and the small ones sort where they belong instead of
 * crowding page one ahead of rows eighty times their size.
 */
const DEFAULT_PAGE_SIZE = 20;

/** The unrecorded-specialty label (FR-S31). */
const UNRECORDED = '(UNRECORDED)';

interface Spec {
  key: keyof SpecialtyMetrics;
  label: string;
  fullLabel: string;
  accent?: string;
  bold?: boolean;
}

const SPECS: Spec[] = [
  { key: 'bob', label: 'BOB', fullLabel: 'Book of Business — total candidates', bold: true },
  { key: 'activeToa', label: 'ACTIVE', fullLabel: 'Active Travelers on Assignment', accent: seriesColor.toa },
  { key: 'toaInternal', label: 'ACTV-I', fullLabel: 'Active — Internal (same region)', accent: color.nursing },
  { key: 'toaTarget', label: 'ACTV-T', fullLabel: 'Active — Target Account', accent: color.colTarget },
  { key: 'onboarding', label: 'ONB', fullLabel: 'Onboarding', accent: color.positive },
  { key: 'prospect', label: 'PROS', fullLabel: 'Prospect', accent: color.colProspect },
  { key: 'pending', label: 'PEND', fullLabel: 'Pending', accent: color.warning },
  { key: 'prevEmployed', label: 'PREV. EMP', fullLabel: 'Previously Employed', accent: color.inkMuted },
];

/**
 * One line, ellipsised — the blueprint's PROF and SPECIALTY cells.
 *
 * Without this a long label (`SONOGRAPHER/ULTRASOUND TECH`, `PATIENT CARE TECHNICIANS`) wraps and
 * doubles that row's height, so the table reads as ragged bands rather than a scannable ranking.
 * At 224 rows the uneven rhythm is the difference between a list you can scan and one you cannot.
 */
const clamp = {
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

/**
 * The text columns' own padding, matching `<MetricCell>`'s so all eleven share one baseline.
 * Without it these three cells contribute no height and sit flush against the row divider while
 * the eight numeric ones are inset.
 */
const cellPad = '8px 4px';

/**
 * A clamped label that reveals its full text on hover **only when it is actually clipped**.
 *
 * ⚠ A plain `title`, not `<Disclosure>`, and the distinction is the point. `<Disclosure>` exists
 * to reveal information that is *not otherwise in the document* — the full column label behind a
 * short code, the breakdown behind a number — and it earns its focusable button by carrying
 * something a keyboard user could not otherwise reach. Here the full text **is** in the DOM and
 * screen readers already read it in full; only sighted users lose the tail to an ellipsis. Wiring
 * these through `<Disclosure>` would add two tab stops per row — forty per page — to expose
 * nothing new.
 *
 * The title is applied conditionally because `SPECIALTY` fits in the great majority of rows, and
 * a tooltip that fires on every cell makes the table unpleasant to move a pointer across.
 */
function ClampedText({ text, style }: { text: string; style?: CSSProperties }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [clipped, setClipped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The 1px slack absorbs sub-pixel rounding, which otherwise reports a perfectly-fitting
    // label as overflowing at some zoom levels and window widths.
    const measure = (): void => setClipped(el.scrollWidth > el.clientWidth + 1);
    measure();

    // Column widths are fractional, so clipping changes with the window and with the sort — not
    // only when the text does. Absent in jsdom, where the measurement is meaningless anyway.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <span ref={ref} title={clipped ? text : undefined} style={{ ...clamp, ...style }}>
      {text}
    </span>
  );
}

function TypeChip({ row }: { row: SpecialtyRow }) {
  const isNursing = row.offering === 'nursing';
  const { nursing, allied } = row.offeringSplit;
  // The exact split on hover: 3 of 174 disciplines carry a single stray candidate of the other
  // offering, and a bare chip on those three would assert 99.7% as if it were 100% (FR-S10).
  const title = `Nursing ${nursing} · Allied ${allied}`;
  return (
    <span
      title={title}
      style={{
        fontSize: fontSize.micro,
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: radius.sm,
        // Mixed case, as the blueprint renders it. Uppercasing turns a quiet type marker into a
        // second row-level shout competing with the SPECIALTY cell beside it.
        color: isNursing ? color.nursing : color.allied,
        // Measured 1.65:1 and 2.30:1 in dark before this (2026-08-29): the wash stayed a light-mode
        // pastel while `color.nursing`/`color.allied` lightened with the theme, so text and
        // background moved the same way and the badge went nearly blank.
        background: isNursing ? color.washTeal : color.washIndigo,
      }}
    >
      {isNursing ? 'Nursing' : 'Allied'}
    </span>
  );
}

export function SpecialtyTable({
  rows,
  rowCount,
  selectedKey,
  onSelect,
  pageSize = DEFAULT_PAGE_SIZE,
  emptyMessage = 'No specialties match this filter.',
}: SpecialtyTableProps) {
  const [page, setPage] = useState(1);

  // Back to page one whenever the row set itself changes. Without this, an offering toggle that
  // cuts 224 rows to 60 would leave you on page 9 of 3 — a blank table that reads as "no data".
  // `rows` is memoised upstream, so this fires on a real filter change and not on every render.
  useEffect(() => {
    setPage(1);
  }, [rows]);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  // Clamped rather than trusted: a re-render can arrive with a shorter `rows` before the effect
  // above runs, and slicing past the end would flash an empty page.
  const current = Math.min(page, pageCount);
  const start = (current - 1) * pageSize;
  const shown = useMemo(() => rows.slice(start, start + pageSize), [rows, start, pageSize]);

  const sum = (list: SpecialtyRow[], key: 'bob' | 'activeToa'): number =>
    list.reduce((n, r) => n + (r.metrics[key].value ?? 0), 0);

  // Everything not on this page — the whole row set minus what is rendered, NOT "the rows after
  // this one". With pagination the off-page set straddles the visible window on both sides
  // (FR-S12: the shortfall against the cards above must never read as an arithmetic fault).
  const offPage = rows.length - shown.length;
  const offPageBob = sum(rows, 'bob') - sum(shown, 'bob');
  const offPageToa = sum(rows, 'activeToa') - sum(shown, 'activeToa');

  const columns: Array<Column<SpecialtyRow>> = [
    {
      key: 'profession',
      label: 'PROF',
      fullLabel: 'Profession — the credential the candidate holds',
      width: 'minmax(96px, 1.1fr)',
      align: 'left',
      sortable: true,
      sortValue: (r) => r.profession,
      render: (r) => (
        <ClampedText
          text={r.profession}
          style={{ padding: cellPad, fontSize: fontSize.body, color: color.inkMuted }}
        />
      ),
    },
    {
      key: 'specialty',
      label: 'SPECIALTY',
      fullLabel: 'Specialty — the clinical area of work',
      width: 'minmax(150px, 1.6fr)',
      align: 'left',
      sortable: true,
      sortValue: (r) => r.specialty,
      render: (r) => (
        <ClampedText
          text={r.specialty === UNRECORDED ? 'Specialty not recorded' : r.specialty}
          style={{
            padding: cellPad,
            fontSize: fontSize.bodyStrong,
            fontWeight: 600,
            color: color.ink,
          }}
        />
      ),
    },
    {
      key: 'type',
      label: 'TYPE',
      fullLabel: 'Nursing or Allied, derived from the profession',
      width: 'minmax(78px, .8fr)',
      align: 'left',
      sortable: true,
      sortValue: (r) => r.offering,
      render: (r) => (
        <div style={{ padding: cellPad }}>
          <TypeChip row={r} />
        </div>
      ),
    },
    ...SPECS.map<Column<SpecialtyRow>>((spec) => ({
      key: spec.key,
      label: spec.label,
      fullLabel: spec.fullLabel,
      align: 'right',
      sortable: true,
      // A null sorts last in BOTH directions rather than ranking as 0 — "we do not know" is not
      // a small number (FR-S27).
      sortValue: (r) => r.metrics[spec.key].value,
      render: (r) => (
        <MetricCell
          metric={r.metrics[spec.key]}
          label={spec.fullLabel}
          metricKey={spec.key}
          bold={spec.bold}
          colorOverride={spec.accent}
          // ⚠ No arrow anywhere on this page. An arrow asserts a comparison against a prior
          // window, and this page holds none (FR-S16).
          showTrendArrow={false}
        />
      ),
    })),
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={shown}
        rowKey={specialtyRowKey}
        onRowClick={onSelect}
        isRowSelected={(r) => specialtyRowKey(r) === selectedKey}
        // BoB descending — the blueprint's `specSortKey:'count', desc` (FR-S33, spec §6.7).
        //
        // ⚠ Declaring it matters even though the API already sends rows in this order: without it
        // every header reports `aria-sort="none"`, so the table is silently ranked by a column it
        // does not name. The sort is stable, so the server's specialty-then-profession tie-break
        // survives re-sorting by the same key.
        initialSort={{ key: 'bob', direction: 'desc' }}
        emptyMessage={emptyMessage}
        caption="Disciplines by profession and specialty"
      />

      {rows.length > 0 && (
        /**
         * ⚠ **The off-page counts are required, not decorative** (FR-S12). Twenty rows of 224 hold
         * a fraction of the book, so they never sum to the offering cards above them. Without this
         * line that shortfall reads as an arithmetic fault; with it, it reads as a setting.
         */
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: space['4xl'],
            flexWrap: 'wrap',
            padding: `${space['3xl']} ${space['5xl']}`,
            borderTop: `1px solid ${color.borderSubtle}`,
            fontSize: fontSize.caption,
            color: color.inkMuted,
          }}
        >
          <span aria-live="polite">
            Showing <span style={{ fontFamily: font.mono }}>{start + 1}</span>–
            <span style={{ fontFamily: font.mono }}>{start + shown.length}</span> of{' '}
            <span style={{ fontFamily: font.mono }}>{rowCount}</span> disciplines
            {offPage > 0 && (
              <>
                {' · '}
                <span style={{ fontFamily: font.mono }}>{offPage}</span> not shown (
                <span style={{ fontFamily: font.mono }}>{formatNumber(offPageBob)}</span>{' '}
                candidates,{' '}
                <span style={{ fontFamily: font.mono }}>{formatNumber(offPageToa)}</span>{' '}
                on assignment)
              </>
            )}
          </span>

          {pageCount > 1 && (
            <Pager page={current} pageCount={pageCount} onGo={setPage} />
          )}
        </div>
      )}
    </>
  );
}

/**
 * Page controls.
 *
 * Numbered rather than Prev/Next alone because the rows are ranked by book size: "page 6 of 12"
 * tells you roughly how far down the ranking you are, which is the thing a reader is actually
 * navigating by. The window slides so the control keeps a fixed width across all twelve pages
 * instead of reflowing the footer every click.
 */
function Pager({
  page,
  pageCount,
  onGo,
}: {
  page: number;
  pageCount: number;
  onGo: (page: number) => void;
}) {
  // At most five numbers, centred on the current page and clamped at both ends.
  const first = Math.max(1, Math.min(page - 2, pageCount - 4));
  const numbers = Array.from({ length: Math.min(5, pageCount) }, (_unused, i) => first + i);

  const step = (label: string, to: number, disabled: boolean) => (
    <button
      type="button"
      onClick={() => onGo(to)}
      disabled={disabled}
      aria-label={label === '‹' ? 'Previous page' : 'Next page'}
      style={{
        ...pageButton,
        color: disabled ? color.inkHint : color.ink,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {label}
    </button>
  );

  return (
    <nav aria-label="Pagination" style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
      {step('‹', page - 1, page === 1)}
      {numbers.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onGo(n)}
          // The screen reader gets the state; sighted users get the fill.
          aria-current={n === page ? 'page' : undefined}
          aria-label={`Page ${n}`}
          style={{
            ...pageButton,
            fontFamily: font.mono,
            fontWeight: n === page ? 700 : 500,
            color: n === page ? color.ink : color.inkMuted,
            background: n === page ? color.surfaceSorted : color.surface,
            borderColor: n === page ? color.borderStrong : color.borderChip,
            cursor: 'pointer',
          }}
        >
          {n}
        </button>
      ))}
      {step('›', page + 1, page === pageCount)}
    </nav>
  );
}

const pageButton = {
  minWidth: 30,
  border: `1px solid ${color.borderChip}`,
  background: color.surface,
  borderRadius: radius.md,
  padding: `${space.md} ${space.lg}`,
  fontSize: fontSize.caption,
  color: color.ink,
} as const;
